import {FormControl} from "./FormControl";

/**
 * Dropdown component for rendering a select input with customizable options.
 *
 * Props:
 * @param {Object[]} options - Array of option objects to display in the dropdown.
 * @param {Function} onChange - Callback function called when the selected value changes.
 * @param {boolean} [disabled] - If true, disables the dropdown.
 * @param {string} [placeholder] - Placeholder text displayed as the first option.
 * @param {string} [label] - Label text displayed above the dropdown.
 * @param {string} [valueProperty="id"] - Property name in option objects to use as the value.
 * @param {string} [property="name"] - Property name in option objects to use as the display text.
 * @param {boolean} [required] - If true, marks the dropdown as required.
 * @param {string|number} [value] - The currently selected value.
 * @param {Function} [nameAggregate] - Function to generate display text for each option.
 * @param {string|null} [error=null] - Error message to display.
 * @param {string} [disabledOptionKey="disabled"] - Property name in option objects to determine if an option is disabled.
 * @param {string} [emptyOption] - If provided, renders a selectable empty option with this label at the top of the list (value="").
 */
export const Dropdown = ({ options, onChange, disabled, placeholder, label, valueProperty = "id", property = "name", required, value, nameAggregate, error = null, disabledOptionKey = "disabled", emptyOption }) => {
    return <FormControl controls={{required, error}}>
        {label && <label>{label}</label>}
        <select disabled={disabled}
                className="form-control rounded-8 mr-16"
                value={value ?? -1}
                onChange={(e) => {
                    const selectedOption = options.find(opt => opt[valueProperty] === e.target.value);
                    if (selectedOption?.[disabledOptionKey]) {
                        return;
                    }
                    onChange(e.target.value);
                }}
        >
            <option disabled hidden value={-1}>{placeholder}</option>
            {emptyOption !== undefined && <option value="">{emptyOption}</option>}
            {options && Object.values(options)?.map((item, index) => 
                <option key={item[valueProperty]}
                        value={item[valueProperty]}
                        disabled={item[disabledOptionKey]}
                >
                    {nameAggregate ? nameAggregate(item) : item[property]}
            </option>)}
        </select>
    </FormControl>
}